import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import CreatableSelect from 'react-select/creatable';
import PhoneInput from 'react-phone-number-input';
import { CountrySelect } from '../../components/ui/country-select';
import { useWorkspace } from '../../context/workspace-context';
import { useUpdateUser, useUser } from '../../hooks/use-user';
import { getRoleId } from '../../helpers/get-role-id';
import { stripPhonePrefix } from '../../helpers/strip-phone-prefix';

interface FormData {
  name: string;
  surname: string;
  email: string;
  role: string;
  status: string;
}

export default function EditUserPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    surname: '',
    email: '',
    role: '',
    status: '',
  });
  const [phone, setPhone] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  const updateUserMutation = useUpdateUser();
  const {
    data: userData,
    isLoading: isFetchingUser,
    error: fetchError,
  } = useUser(id ? Number(id) : undefined, currentWorkspace?.id);

  // Populate form when user data loads
  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.user.name,
        surname: userData.user.surname,
        email: userData.user.email || '',
        role: userData.user.roles.name,
        status: userData.user.status,
      });
      setPhone(userData.user.phone ? `+${userData.user.phone}` : undefined);
      setSelectedDepartments(userData.userDepartments);
    }
  }, [userData]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { name: string; value: string } },
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!currentWorkspace || isLoadingWorkspaces) {
      return;
    }

    const phoneDigits = phone ? stripPhonePrefix(phone) : undefined;

    updateUserMutation.mutate(
      {
        userId: Number(id),
        organisationId: currentWorkspace.id,
        name: formData.name,
        surname: formData.surname,
        email: formData.email || undefined,
        phone: phoneDigits,
        roleId: getRoleId(formData.role),
        status: formData.status,
        departments: selectedDepartments,
      },
      {
        onSuccess: () => {
          setSuccessMessage('User updated successfully!');
          setTimeout(() => {
            navigate('/users');
          }, 2000);
        },
      },
    );
  };

  if (isFetchingUser || isLoadingWorkspaces) {
    return <div>Loading user data...</div>;
  }

  if (fetchError) {
    return (
      <div className="h-full">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Edit User</h1>
        </div>
        <div className="p-4 text-center text-destructive">
          {fetchError instanceof Error
            ? fetchError.message
            : 'An error occurred'}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Edit User</h1>
      </div>
      <div className="w-full max-w-4xl mx-auto">
        <Card className="w-full bg-white shadow-none">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">First Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John"
                    required
                    className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="surname">Last Name</Label>
                  <Input
                    id="surname"
                    name="surname"
                    type="text"
                    value={formData.surname}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    required
                    className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                  style={{ fontSize: '16px' }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <PhoneInput
                  id="phone"
                  defaultCountry="ZA"
                  international
                  countryCallingCodeEditable={false}
                  value={phone}
                  onChange={setPhone}
                  placeholder="79 123 4567"
                  inputComponent={Input}
                  countrySelectComponent={CountrySelect}
                  numberInputProps={{
                    className:
                      'h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none',
                    style: { fontSize: '16px' },
                  }}
                  className="flex gap-2 items-center"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => handleSelectChange('role', value)}
                    required
                  >
                    <SelectTrigger className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none">
                      <SelectValue placeholder="Select Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Employee">Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      handleSelectChange('status', value)
                    }
                    required
                  >
                    <SelectTrigger className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="departments">Team / Department</Label>
                <CreatableSelect
                  isMulti
                  options={userData?.availableDepartments || []}
                  value={selectedDepartments.map((dept) => ({
                    value: dept,
                    label: dept,
                  }))}
                  onChange={(newValue) =>
                    setSelectedDepartments(
                      newValue ? newValue.map((option) => option.value) : [],
                    )
                  }
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
                disabled={updateUserMutation.isPending}
              >
                {updateUserMutation.isPending
                  ? 'Updating User...'
                  : 'Update User'}
              </Button>

              {updateUserMutation.error && (
                <div className="p-3 text-sm text-center text-destructive">
                  {updateUserMutation.error instanceof Error
                    ? updateUserMutation.error.message
                    : 'An error occurred'}
                </div>
              )}

              {successMessage && (
                <div className="p-3 text-sm text-center text-green-800">
                  {successMessage}
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
